import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View, Pressable, Platform, Animated } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import BannerCountdown from '@/components/Banner/BannerCountdown';
import { useModalStore } from '@/store';
import { isDarkTheme, getBannerAccent } from '@/constants/Colors';
import { BORDER_RADIUS } from '@/constants/config';
import { trackEvent } from '@/constants/utils/analytics';
import { executeBannerCta } from '@/lib/bannerActions';
import useResponsiveness from '@/hooks/useResponsiveness';
import { bannerFallbackIcon, bannerSurface, webInteractive, withAlpha } from '@/components/Banner/bannerChrome';

const createStyles = (colors, isDark, accent, compact) => {
  const surface = bannerSurface(colors, isDark, accent);

  return StyleSheet.create({
    outer: {
      width: '100%',
      paddingHorizontal: compact ? 8 : 12,
      paddingVertical: compact ? 6 : 8,
    },
    card: {
      ...surface,
      position: 'relative',
    },
    accentRail: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 3,
      backgroundColor: accent,
    },
    inner: {
      flexDirection: compact ? 'column' : 'row',
      alignItems: compact ? 'stretch' : 'center',
      paddingVertical: compact ? 10 : 10,
      paddingHorizontal: compact ? 10 : 12,
      paddingLeft: compact ? 12 : 14,
      gap: compact ? 8 : 12,
      minHeight: compact ? 0 : 44,
    },
    topRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: compact ? 8 : 10,
    },
    iconWell: {
      width: compact ? 26 : 28,
      height: compact ? 26 : 28,
      borderRadius: compact ? 13 : 14,
      backgroundColor: withAlpha(accent, isDark ? 0.22 : 0.14),
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    content: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: compact ? 6 : 8,
      flexWrap: 'wrap',
      minWidth: 0,
    },
    textCol: {
      flex: 1,
      gap: 2,
      minWidth: 0,
    },
    badge: {
      paddingHorizontal: compact ? 6 : 7,
      paddingVertical: 2,
      borderRadius: BORDER_RADIUS.round,
      backgroundColor: withAlpha(accent, isDark ? 0.22 : 0.12),
      alignSelf: 'flex-start',
    },
    promoCodePill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: compact ? 7 : 8,
      paddingVertical: 3,
      borderRadius: BORDER_RADIUS.small + 2,
      backgroundColor: withAlpha(accent, isDark ? 0.16 : 0.08),
      borderWidth: 1,
      borderColor: withAlpha(accent, isDark ? 0.32 : 0.2),
      borderStyle: 'dashed',
    },
    promoCodePillPressable: {
      ...webInteractive,
    },
    promoCodePillHover: {
      backgroundColor: withAlpha(accent, isDark ? 0.26 : 0.14),
      borderColor: withAlpha(accent, isDark ? 0.45 : 0.32),
    },
    bottomRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      paddingLeft: compact ? 0 : 38,
    },
    bottomMeta: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
      flex: 1,
    },
    countdownWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 7,
      paddingVertical: 3,
      borderRadius: BORDER_RADIUS.round,
      backgroundColor: colors.solid_color_tertiary,
    },
    ctaBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: compact ? 10 : 12,
      paddingVertical: compact ? 5 : 6,
      borderRadius: BORDER_RADIUS.small + 2,
      backgroundColor: withAlpha(accent, isDark ? 0.22 : 0.12),
      borderWidth: 1,
      borderColor: withAlpha(accent, isDark ? 0.28 : 0.16),
      flexShrink: 0,
      ...webInteractive,
    },
    ctaBtnHovered: {
      backgroundColor: withAlpha(accent, isDark ? 0.32 : 0.2),
    },
    dismissBtn: {
      width: compact ? 26 : 28,
      height: compact ? 26 : 28,
      borderRadius: compact ? 13 : 14,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      ...webInteractive,
    },
    dismissBtnHover: {
      backgroundColor: colors.solid_color_tertiary,
    },
  });
};

function AnnouncementBanner({ banner, onMarkSeen, onCtaClick, onDismiss, isCompact = false, style }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const { isSM } = useResponsiveness();
  const hasFiredSeen = useRef(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideY = useRef(new Animated.Value(-10)).current;

  const { id, contentType, content, placement, isDismissible, countdown, promoCode } = banner;
  const compact = isCompact || isSM;
  const accent = useMemo(
    () => getBannerAccent(contentType, isDark, colors),
    [contentType, isDark, colors],
  );
  const styles = useMemo(
    () => createStyles(colors, isDark, accent, compact),
    [colors, isDark, accent, compact],
  );
  const iconName = content.icon || bannerFallbackIcon(contentType);

  useEffect(() => {
    if (!hasFiredSeen.current) {
      hasFiredSeen.current = true;
      onMarkSeen?.(id);
    }
  }, [id, onMarkSeen]);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 1, duration: 220, useNativeDriver: true }),
      Animated.spring(slideY, { toValue: 0, tension: 65, friction: 11, useNativeDriver: true }),
    ]).start();
  }, []);

  const handleCTA = useCallback(() => {
    trackEvent('conversion', 'banner_cta_clicked', placement, id);
    onCtaClick?.(id);
    executeBannerCta(banner, { router, openModal });
  }, [banner, placement, id, router, openModal, onCtaClick]);

  const handleDismiss = useCallback(() => {
    trackEvent('engagement', 'banner_dismissed', placement, id);
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 0, duration: 180, useNativeDriver: true }),
      Animated.timing(slideY, { toValue: -10, duration: 180, useNativeDriver: true }),
    ]).start(() => onDismiss?.(id));
  }, [id, placement, onDismiss, fadeAnim, slideY]);

  const handleCountdownExpired = useCallback(() => {
    onDismiss?.(id);
  }, [id, onDismiss]);

  const hasBottomRow = compact && (promoCode?.code || (countdown?.enabled && countdown?.endsAt) || content.ctaText);

  const dismissButton = isDismissible ? (
    <Pressable
      onPress={handleDismiss}
      accessibilityRole="button"
      accessibilityLabel="Dismiss"
      hitSlop={6}
      style={({ hovered, pressed }) => [
        styles.dismissBtn,
        Platform.OS === 'web' && hovered && styles.dismissBtnHover,
        pressed && { opacity: 0.85 },
      ]}
    >
      <Icon name="xmark" size={11} color={colors.text_color_tertiary} />
    </Pressable>
  ) : null;

  const iconWell = (
    <View style={styles.iconWell}>
      <Icon name={iconName} size={compact ? 12 : 13} color={accent} solid />
    </View>
  );

  const badge = content.badgeText ? (
    <View style={styles.badge}>
      <Text size="xxs" weight="bold" style={{ color: accent, letterSpacing: 0.4 }}>
        {content.badgeText}
      </Text>
    </View>
  ) : null;

  const promoPill = promoCode?.code ? (
    <Pressable
      onPress={handleCTA}
      accessibilityRole="button"
      accessibilityLabel={`Apply offer, code ${promoCode.code}`}
      style={({ hovered, pressed }) => [
        styles.promoCodePill,
        styles.promoCodePillPressable,
        Platform.OS === 'web' && hovered && styles.promoCodePillHover,
        pressed && { opacity: 0.9 },
      ]}
    >
      <Icon name="tag" size={10} color={accent} />
      <Text size="xs" weight="bold" style={{ color: accent }}>{promoCode.code}</Text>
    </Pressable>
  ) : null;

  const countdownRow = countdown?.enabled && countdown?.endsAt ? (
    <View style={styles.countdownWrap}>
      <Icon name="clock" size={10} color={colors.text_color_tertiary} />
      <BannerCountdown
        endsAt={countdown.endsAt}
        onExpired={handleCountdownExpired}
        textColor={colors.text_color_secondary}
        textSize="xs"
      />
    </View>
  ) : null;

  const ctaButton = content.ctaText ? (
    <Pressable
      onPress={handleCTA}
      accessibilityRole="button"
      accessibilityLabel={content.ctaText}
    >
      {({ hovered, pressed }) => (
        <View style={[styles.ctaBtn, hovered && styles.ctaBtnHovered, pressed && { opacity: 0.88 }]}>
          <Text size="xs" weight="bold" style={{ color: accent }}>{content.ctaText}</Text>
          <Icon name="arrow-right" size={10} color={accent} />
        </View>
      )}
    </Pressable>
  ) : null;

  return (
    <Animated.View style={[styles.outer, style, { opacity: fadeAnim, transform: [{ translateY: slideY }] }]}>
      <View style={styles.card}>
        <View style={styles.accentRail} />
        <View style={styles.inner}>
          {compact ? (
            <>
              <View style={styles.topRow}>
                {iconWell}
                <View style={styles.textCol}>
                  {badge}
                  {!!content.headline && (
                    <Text size="s" weight="bold" color="primary">{content.headline}</Text>
                  )}
                  {!!content.subtitle && (
                    <Text size="xs" color="secondary">{content.subtitle}</Text>
                  )}
                </View>
                {dismissButton}
              </View>
              {hasBottomRow && (
                <View style={styles.bottomRow}>
                  <View style={styles.bottomMeta}>
                    {promoPill}
                    {countdownRow}
                  </View>
                  {ctaButton}
                </View>
              )}
            </>
          ) : (
            <>
              {iconWell}
              <View style={styles.content}>
                {badge}
                {!!content.headline && (
                  <Text size="s" weight="bold" color="primary">{content.headline}</Text>
                )}
                {!!content.subtitle && (
                  <Text size="xs" color="secondary">{content.subtitle}</Text>
                )}
                {promoPill}
                {countdownRow}
              </View>
              {ctaButton}
              {dismissButton}
            </>
          )}
        </View>
      </View>
    </Animated.View>
  );
}

export default React.memo(AnnouncementBanner);
